import React, { useEffect, useRef, useState } from 'react';

import { parseBRL, formatBRLInput } from '../../utils/money';

interface MoneyInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value: number | null | undefined;
  onValueChange: (value: number | null) => void;
}

// Comportamento: "tipo banco" — internalizamos apenas dígitos representando centavos.
// Ao digitar, os dígitos empurram os centavos para a esquerda. Cursor é alinhado ao fim.
const MoneyInput: React.FC<MoneyInputProps> = ({ value, onValueChange, className, placeholder, disabled, ...rest }) => {
  const [digitsOnly, setDigitsOnly] = useState<string>('0'); // centavos como string
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Sincroniza valor externo -> estado interno
  useEffect(() => {
    if (value === null || value === undefined || Number.isNaN(value)) {
      setDigitsOnly('0');
      return;
    }
    const cents = Math.round(value * 100);
    setDigitsOnly(String(Math.abs(cents)));
  }, [value]);

  const syncAndNotify = (newDigits: string) => {
    const limited = newDigits.replace(/^0+(?=\d)/, '').slice(0, 15) || '0';
    setDigitsOnly(limited);
    const n = parseInt(limited, 10);
    if (isNaN(n)) onValueChange(null);
    else onValueChange(n / 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = digitsOnly.length <= 1 ? '0' : digitsOnly.slice(0, -1);
      syncAndNotify(next);
      // mantém cursor no final
      setTimeout(() => inputRef.current && inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length), 0);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const raw = e.target.value || '';
    // extrai apenas dígitos; isso permite o usuário digitar em qualquer formato (mas exibimos formatado)
    const onlyDigits = raw.replace(/\D/g, '') || '0';
    syncAndNotify(onlyDigits);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.preventDefault();
    const text = e.clipboardData.getData('Text') || '';
    const parsed = parseBRL(text);
    if (parsed === null) {
      const onlyDigits = text.replace(/\D/g, '') || '0';
      syncAndNotify(onlyDigits);
    } else {
      const cents = Math.round(parsed * 100);
      syncAndNotify(String(Math.abs(cents)));
    }
    setTimeout(() => inputRef.current && inputRef.current.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length), 0);
  };

  const display = formatBRLInput(digitsOnly ? (parseInt(digitsOnly, 10) / 100) : 0);

  useEffect(() => {
    if (inputRef.current) {
      const len = inputRef.current.value.length;
      inputRef.current.setSelectionRange(len, len);
    }
  }, [display]);

  return (
    <input
      {...rest}
      ref={inputRef}
      inputMode="numeric"
      type="text"
      value={display}
      onKeyDown={handleKeyDown}
      onChange={handleChange}
      onPaste={handlePaste}
      className={className}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
};

export default MoneyInput;
